import React, { useState } from 'react';
import { Typography } from '~/UIKit';
import { useTranslation } from 'react-i18next';
import { COLORS } from './ColorPicker.constants';
import { ColorButton } from '~/UIKit/ColorButton';
import { Divider } from '~/UIKit/Text';
import { ColorSlider } from './ColorSlider';
import { HexColorInput } from './HexColorInput';
import { PopoverToModal } from '~/Components';
import { makeStyles } from '~/UIKit/Style';
import { PRIMARY_BLUE } from '~/Constants/Colors.constants';
import { FONT_SIZE_LARGE } from '~/Constants/UI.constants';

interface ColorPickerModalProps {
  open: boolean;
  anchorEl: Element | null;
  onClose: () => void;
  updateColor: (color: string) => void;
  currentColor: string;
}

const useStyles = makeStyles(
  (theme) => ({
    colorPickerContent: {
      padding: theme.spacing(2),
    },
    divider: {
      margin: `${theme.spacing(3)} -${theme.spacing(2)}`,
    },
    colorsWrapper: {
      display: 'flex',
      flexWrap: 'wrap',
      marginTop: theme.spacing(2),
    },
    colorRow: {
      width: '100%',
      marginBottom: theme.spacing(2),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',

      '&:last-child': {
        marginBottom: 0,
      },
    },
    colorButtonWrapper: {},
    title: {
      fontSize: FONT_SIZE_LARGE,
      fontWeight: 'bold',
    },
  }),
  { name: 'ColorPickerModal' },
);

const NUMBER_OF_COLOR_ROWS = 3;
const ITEMS_PER_ROW = COLORS.length / NUMBER_OF_COLOR_ROWS;

export const ColorPickerModal = ({
  open,
  anchorEl,
  onClose,
  updateColor,
  currentColor = PRIMARY_BLUE,
}: ColorPickerModalProps) => {
  const { classes } = useStyles();
  const { t } = useTranslation();
  const [color, setColor] = useState(currentColor);

  const handleColorUpdate = (currentColor: string) => {
    setColor(currentColor);
    updateColor(currentColor);
  };

  const onActionTaken = () => {
    updateColor(color);
    onClose();
  };

  return (
    <PopoverToModal
      onActionTaken={onActionTaken}
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      title={t('colorpicker.title')}
      actionName={t('app.save')}
    >
      <div className={classes.colorPickerContent}>
        <Typography className={classes.title}>{t('colorpicker.select')}</Typography>
        <div className={classes.colorsWrapper}>
          {new Array(NUMBER_OF_COLOR_ROWS).fill(null).map((_value, currentRowIndex) => (
            <div className={classes.colorRow} key={`color-row-${currentRowIndex}`}>
              {new Array(ITEMS_PER_ROW).fill(null).map((_rowValue, currentItemIndex) => {
                const currentColor = COLORS[currentRowIndex * ITEMS_PER_ROW + currentItemIndex];
                const isButtonSelected = color === currentColor;
                return (
                  !!currentColor && (
                    <div
                      className={classes.colorButtonWrapper}
                      key={`color-button-${currentRowIndex}-${currentItemIndex}`}
                    >
                      <ColorButton
                        data-testid={`colorpickermodal-color-${currentRowIndex}-${currentItemIndex}`}
                        color={currentColor}
                        onClick={() => handleColorUpdate(currentColor)}
                        isButtonSelected={isButtonSelected}
                      />
                    </div>
                  )
                );
              })}
            </div>
          ))}
        </div>
        <Divider className={classes.divider} />
        <Typography className={classes.title}>{t('colorpicker.createOwn')}</Typography>
        <ColorSlider color={currentColor} onChange={handleColorUpdate} />
        <HexColorInput color={color} setColor={handleColorUpdate} />
      </div>
    </PopoverToModal>
  );
};
